import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import PageHero from "../components/PageHero";
import { Reveal } from "../components/motion";
import { BRAND, IMAGES } from "../lib/brand";
import { px } from "../lib/api";

const STATS = [
  { n: "12hrs", l: "Marination time" },
  { n: "100%", l: "Fresh, never frozen" },
  { n: "4.8★", l: "Avg. delivery rating" },
  { n: "Noida", l: "Proudly local" },
];

const TEAM = [
  {
    name: "Ranveer Ahluwalia",
    role: "Founder & CEO",
    img: "https://images.pexels.com/photos/2379004/pexels-photo-2379004.jpeg",
    bio: "Ex-corporate turned kukkad-obsessed. Left the cubicle to build the delivery brand he always wished existed.",
  },
  {
    name: "Chef Imtiyaz Khan",
    role: "Head Chef",
    img: "https://images.pexels.com/photos/3814446/pexels-photo-3814446.jpeg",
    bio: "20 years across Lucknawi & Mughlai kitchens. Guardian of the secret marinade and the 24-hour dal.",
  },
  {
    name: "Simran Kaur",
    role: "Head of Experience",
    img: "https://images.pexels.com/photos/1181686/pexels-photo-1181686.jpeg",
    bio: "Makes sure every box that leaves is 'gram-worthy and every customer feels like family.",
  },
];

export default function About() {
  return (
    <div data-testid="about-page" className="pb-32">
      <PageHero
        testid="about-hero"
        eyebrow="Est. 2025 · Noida"
        title="Our"
        accent="Story"
        subtitle="A tiny kitchen, a big attitude, and a cheeky chick who believes delivery food should never taste like a compromise."
      />

      {/* Story split */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20 grid lg:grid-cols-2 gap-12 items-center">
        <Reveal>
          <div className="rounded-lg overflow-hidden border border-border shadow-hard">
            <img src={px(IMAGES.friends, 1200)} alt="Friends sharing a Kukkad Co. meal" className="w-full object-cover" />
          </div>
        </Reveal>
        <Reveal delay={0.1}>
          <h2 className="font-heading uppercase text-4xl sm:text-5xl tracking-tighter leading-none">
            Built for the <span className="text-orange">high-rise</span> generation
          </h2>
          <p className="text-cream/70 mt-5 text-lg">
            The Kukkad Co. started with a simple frustration — great food that fell apart by the time it reached the door. So we obsessed over the details: longer marinations, real coal tandoors, and packaging engineered to keep the crunch alive.
          </p>
          <p className="text-cream/70 mt-4 text-lg">
            Today we cook for the Millennials pulling late shifts, the Gen-Z squads ordering for movie night, and the families in Noida’s towers who just want dinner done right.
          </p>
        </Reveal>
      </section>

      {/* Stats */}
      <section className="bg-brown-deep border-y border-border">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-14 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {STATS.map((s, i) => (
            <Reveal key={s.l} delay={i * 0.08} className="text-center">
              <p className="font-heading text-orange text-5xl sm:text-6xl">{s.n}</p>
              <p className="text-cream/60 uppercase text-sm tracking-widest mt-2">{s.l}</p>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Team */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20">
        <Reveal>
          <p className="font-accent text-chick text-2xl">The people behind the pluck</p>
          <h2 className="font-heading uppercase text-5xl sm:text-6xl tracking-tighter leading-none mt-2">Meet the team</h2>
        </Reveal>
        <div className="grid md:grid-cols-3 gap-6 mt-12">
          {TEAM.map((t, i) => (
            <Reveal key={t.name} delay={i * 0.1}>
              <div data-testid={`team-${i}`} className="group bg-card border border-border rounded-lg overflow-hidden hover:border-orange transition-colors">
                <div className="aspect-[4/5] overflow-hidden">
                  <img src={px(t.img, 700)} alt={t.name} className="w-full h-full object-cover grayscale group-hover:grayscale-0 transition-all duration-500 group-hover:scale-105" />
                </div>
                <div className="p-5">
                  <h3 className="font-heading uppercase text-2xl tracking-tight">{t.name}</h3>
                  <p className="text-orange font-bold uppercase text-sm">{t.role}</p>
                  <p className="text-cream/60 text-sm mt-2">{t.bio}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-[1400px] mx-auto px-5 sm:px-8">
        <Reveal className="rounded-lg bg-orange text-primary-foreground p-10 sm:p-14 flex flex-col sm:flex-row items-center justify-between gap-6">
          <h2 className="font-heading uppercase text-4xl sm:text-5xl tracking-tighter leading-none text-center sm:text-left">
            Tasted the hype yet?
          </h2>
          <Link
            to="/menu"
            data-testid="about-order-cta"
            className="flex items-center gap-2 bg-brown text-cream font-bold uppercase px-8 py-4 rounded-md hover:scale-105 transition-transform shrink-0"
          >
            Order Now <ArrowRight className="w-5 h-5" />
          </Link>
        </Reveal>
      </section>
    </div>
  );
}
